import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Img, { FluidObject } from 'gatsby-image'
import Slider from 'react-slick'

import { Container } from 'components/Container'
import { backgroundColors, colors } from 'styles/colors'
import { displayWidth } from 'styles/width'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { Title } from 'components/TitleComponent'
import { SliderComponent } from 'components/SliderComponent'
import { indent } from 'styles/indent'
import FullScreen from 'assets/icons/fullScreen.svg'
import { ModalCarousel } from 'components/ModalCarousel'
import { ProjectImages } from 'layout/Project'
import { sendEvent } from 'tracking'
import { sendForm } from 'components/form/api'
import { headerBg } from 'styles/headerBg'
import { LeftSidebar, RightSidebar } from 'components/SideBar'
import { Button } from 'components/Button'

const SliderBlockWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.contact};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
    :before {
        ${headerBg}
    }
`

const HeroColumn = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${indent.heroColumnTablet} 24px;
    border-bottom: 1px solid ${colors.dark};
    @media (min-width: ${displayWidth.tablet}) {
        border-bottom: none;
        border-right: 1px solid ${colors.dark};
    }
    @media (min-width: ${displayWidth.desktop}) {
        padding: 0 ${indent.heroColumnDesktop} 24px;
    }
`

const ButtonStyled = styled(Button)`
    a {
        text-decoration: none;
        color: #fff;
        font-size: 18px;
    }
    @media (min-width: ${displayWidth.tablet}) {
        margin: 30px 0;
    }
`

const TitleStyled = styled(Title)`
    @media (min-width: ${displayWidth.tablet}) {
        margin-left: 0;
        margin-right: 0;
    }
`

const CompositionWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.project};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
`

const CompositionTitle = styled.h3`
    font-family: 'Yeseva One', sans-serif;
    font-size: 22px;
    line-height: 28px;
    letter-spacing: 1px;
    margin: 16px auto;
    color: ${colors.dark};
`

const CompositionList = styled.ul`
    margin: 16px;
    list-style: none;
    columns: 1;
    @media (min-width: ${displayWidth.tablet}) {
        columns: 2;
    }
    @media (min-width: ${displayWidth.desktop}) {
        columns: 3;
    }
`

const CompositionItem = styled.li`
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.4px;
    color: ${colors.dark};
    padding: 4px 0;
    break-inside: avoid;
    &:before {
        content: '— ';
        color: ${colors.accentText};
    }
`

const ImgStyled = styled(Img)<{ fluid: FluidObject }>`
    width: 100%;
    height: 100%;
    max-height: 600px;
    img {
        max-height: 600px;
        object-fit: contain;
    }
`

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    background-color: ${colors.white};
    position: relative;
    overflow-x: hidden;
`

const FullScreenButton = styled(FullScreen)`
    fill: ${colors.white};
    stroke: ${colors.dark};
    stroke-width: 1px;
    position: absolute;
    top: 10px;
    right: 26px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 3;
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: block;
    }
    @media (min-width: ${displayWidth.desktop}) {
        right: 10px;
    }
`

interface ImageEdge {
    node: {
        childImageSharp: {
            fluid: FluidObject
            parent: { name: string }
        }
    }
}

interface ImageDataItem {
    title: string
    alt: string
}

interface ArchProjectSliderBlockProps {
    title: string
    images: ImageEdge[]
    imagesData: ImageDataItem[]
    compositionTitle: string
    compositionItems: string[]
    eventPrefix: string
    pdfUrl?: string
    downloadText?: string
    autoOpenModal?: boolean
}

export const ArchProjectSliderBlock = ({
    title,
    images,
    imagesData,
    compositionTitle,
    compositionItems,
    eventPrefix,
    pdfUrl,
    downloadText,
    autoOpenModal = false,
}: ArchProjectSliderBlockProps) => {
    const [isModalOpen, setModalIsOpen] = useState(false)
    const [currentSlideS, setCurrentSlideS] = useState(0)
    const sliderRefD = useRef<Slider | null>(null)
    const sliderD = sliderRefD.current

    const sliderSettings = {
        infinite: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    arrows: false,
                    dots: true,
                },
            },
        ],
    }

    const validImages = images.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => item.node.childImageSharp !== null
    )

    const clearOpenModalParam = () => {
        if (typeof window === 'undefined') {
            return
        }

        const currentUrl = new URL(window.location.href)
        if (!currentUrl.searchParams.has('openModal')) {
            return
        }

        currentUrl.searchParams.delete('openModal')
        const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
        window.history.replaceState(window.history.state, '', nextUrl)
    }

    useEffect(() => {
        if (autoOpenModal) {
            setModalIsOpen(true)
        }
    }, [autoOpenModal])

    return (
        <>
            <SliderBlockWrapper>
                <LeftSidebar background={backgroundColors.contact} />
                <Container columns={'1fr'} tabletColumns={'1fr 2fr'}>
                    <HeroColumn>
                        <TitleStyled>{title}</TitleStyled>
                        {pdfUrl && downloadText && (
                            <ButtonStyled
                                onClick={() => {
                                    sendForm(
                                        `Скачали проект ${eventPrefix}`,
                                        {}
                                    )
                                    sendEvent('Click', {
                                        eventCategory: `DownloadExample${eventPrefix}`,
                                        placement: `ExampleOfProject${eventPrefix}`,
                                    })
                                }}
                                id={`DownloadExample${eventPrefix}`}
                            >
                                <a href={pdfUrl} download>
                                    {downloadText}
                                </a>
                            </ButtonStyled>
                        )}
                    </HeroColumn>
                    <Wrapper>
                        <FullScreenButton
                            onClick={() => {
                                sendEvent('FullScreen', {
                                    eventCategory: `SliderExample${eventPrefix}`,
                                    placement: 'ExampleOfProject',
                                })
                                setModalIsOpen(true)
                            }}
                        />
                        <SliderComponent
                            {...sliderSettings}
                            forwardRef={sliderRefD}
                            afterChange={(current: number) => {
                                setCurrentSlideS(current)
                                sendEvent('ShowSlide', {
                                    eventCategory: `SliderExample${eventPrefix}`,
                                    currentSlide: `${current}`,
                                    placement: 'ExampleOfProject',
                                })
                            }}
                        >
                            {validImages.map(
                                (item: ImageEdge, index: number) => (
                                    <ImgStyled
                                        key={index}
                                        fluid={item.node.childImageSharp.fluid}
                                        imgStyle={{ objectFit: 'contain' }}
                                        alt={imagesData[index]?.alt}
                                        title={imagesData[index]?.title}
                                    />
                                )
                            )}
                        </SliderComponent>
                    </Wrapper>
                </Container>
                <RightSidebar />
                <ModalCarousel
                    data={validImages.map(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ({ node }: any) => node as ProjectImages
                    )}
                    setCurrentSlideS={setCurrentSlideS}
                    isModalOpen={isModalOpen}
                    closeHandler={() => {
                        clearOpenModalParam()
                        setModalIsOpen(false)
                        sliderD !== null && sliderD.slickGoTo(currentSlideS)
                    }}
                    initialSlideIndex={currentSlideS}
                />
            </SliderBlockWrapper>
            <CompositionWrapper>
                <LeftSidebar background={backgroundColors.project} />
                <Container columns={'1fr'} tabletColumns={'1fr'}>
                    <CompositionTitle>{compositionTitle}</CompositionTitle>
                    <CompositionList>
                        {compositionItems.map((item, index) => (
                            <CompositionItem key={index}>
                                {item}
                            </CompositionItem>
                        ))}
                    </CompositionList>
                </Container>
                <RightSidebar background={backgroundColors.project} />
            </CompositionWrapper>
        </>
    )
}
